import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Collapse,
  Divider,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BadgeIcon from "@mui/icons-material/Badge";
import EditIcon from "@mui/icons-material/Edit";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import {
  IPD_COLORS,
  IPD_SHAPE,
  ipdElevatedInputSx,
  ipdLabelSx,
  ipdPageSx,
  IpdButton,
  IpdDetailRow,
  IpdPageHeader,
  IpdSectionCard,
  IpdTabBar,
} from "../../../ipd/utils";
import {
  EmergencyPlaceholder,
  MlcBadge,
  StageBadge,
  TriageBadge,
} from "../../components/EmergencyBadges";
import {
  ARRIVAL_MODE_LABEL,
  formatDateTime,
  visitStage,
  waitingSince,
} from "../../constants";
import {
  getEmergencyVisit,
  recordStageEvent,
  updateEmergencyVisit,
} from "../../api/emergencyApi";
import TriageDialog from "../../components/TriageDialog";
import AssignBedDialog from "../../components/AssignBedDialog";
import ProviderHandoverDialog from "../../components/ProviderHandoverDialog";
import AssessmentCard from "../../components/AssessmentCard";
import VitalsCard from "../../components/VitalsCard";
import DischargeSummaryCard from "../../components/DischargeSummaryCard";
import OrdersCard from "../../components/OrdersCard";
import MedicationsCard from "../../components/MedicationsCard";
import ChargesCard from "../../components/ChargesCard";
import MedicoLegalCard from "../../components/MedicoLegalCard";
import InjuryReportCard from "../../components/InjuryReportCard";
import DispositionCard from "../../components/DispositionCard";
import AllergiesCard from "../../components/AllergiesCard";
import RiskAssessmentCard from "../../components/RiskAssessmentCard";
import HomeMedicationsCard from "../../components/HomeMedicationsCard";
import DeathRegistrationCard from "../../components/DeathRegistrationCard";
import DeathCauseCard from "../../components/DeathCauseCard";
import DeathCertificateCard from "../../components/DeathCertificateCard";
import { TRIAGE } from "../../constants";

const GENDER_LABEL = { M: "Male", F: "Female", T: "Other", O: "Other" };

const PAYER_FIELDS = [
  ["provider", "Insurance provider"],
  ["policy_number", "Policy number"],
  ["tpa", "TPA"],
  ["scheme", "Scheme"],
  ["corporate_sponsor", "Corporate sponsor"],
];

const VISIT_TABS = [
  { id: "overview", label: "Overview" },
  { id: "vitals", label: "Triage & Vitals" },
  { id: "orders", label: "Orders & Results" },
  { id: "meds", label: "Medications Given" },
  { id: "charges", label: "Charges" },
  { id: "mlc", label: "Medico-Legal" },
  { id: "disposition", label: "Disposition" },
];

/**
 * Key clinical timestamps (ER-AUD-003). Steps not yet reached are shown
 * greyed rather than hidden, so the shape of the visit so far is legible —
 * "no bed yet" is information, not absence of information.
 */
function Timeline({ visit }) {
  const steps = [
    ["Arrived", visit.arrived_at],
    ["Triaged", visit.triaged_at],
    ["Bed assigned", visit.bed_assigned_at],
    ["First assessed", visit.assessed_at],
    // Only shown once they happened — most visits involve neither, and
    // permanently pending rows for things that will never occur are noise.
    ...(visit.observation_started_at
      ? [["Under observation", visit.observation_started_at]] : []),
    ["Visit closed", visit.closed_at],
  ];
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {steps.map(([label, at]) => (
        <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 9,
              height: 9,
              flexShrink: 0,
              borderRadius: "50%",
              bgcolor: at ? IPD_COLORS.primary : "transparent",
              border: at ? "none" : `2px solid ${IPD_COLORS.borderHover}`,
            }}
          />
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: at ? 600 : 400,
              color: at ? IPD_COLORS.textPrimary : IPD_COLORS.textMuted,
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              ml: "auto",
              fontSize: 12,
              fontVariantNumeric: "tabular-nums",
              color: IPD_COLORS.textSecondary,
            }}
          >
            {at ? formatDateTime(at) : "—"}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Every assessment ever made, newest first (ER-TRI-008). Shown as a list
 * rather than only the current category so it is visible that a change was
 * recorded, not that an earlier decision was erased.
 */
function TriageHistory({ history }) {
  if (!history || history.length === 0) {
    return (
      <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary }}>
        Not triaged yet. The first assessment will appear here.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
      {history.map((e, i) => {
        const t = TRIAGE[e.category] || {};
        return (
          <Box key={e.id} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                mt: 0.4,
                flexShrink: 0,
                borderRadius: "50%",
                bgcolor: t.color || IPD_COLORS.border,
                opacity: i === 0 ? 1 : 0.45,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: i === 0 ? 700 : 500 }}>
                {t.label || e.category}
                {i === 0 && (
                  <Typography component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 600, color: IPD_COLORS.textSecondary }}>
                    current
                  </Typography>
                )}
              </Typography>
              <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                {formatDateTime(e.triaged_at)}
                {e.triaged_by_name ? ` · ${e.triaged_by_name}` : ""}
              </Typography>
              {e.note && (
                <Typography sx={{ mt: 0.4, fontSize: 12.5, color: IPD_COLORS.textPrimary }}>
                  {e.note}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * Observation (ER-DSP-012a).
 *
 * Presented as a "mid-visit event" and worded so it is unmistakable that it
 * does not end the visit — the most likely misreading of this card is that
 * placing someone under observation closes their emergency visit, and it does
 * not. It is recorded once; once set, the card shows when and by whom rather
 * than offering the button again.
 *
 * The theatre event was removed (ticket 29): a patient needing surgery is
 * admitted, so the surgical pathway is the inpatient admission.
 */
function MidVisitEvents({ visit, onRecord, disabled }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");

  const record = async (event) => {
    setBusy(event);
    setError("");
    try {
      await onRecord(event);
    } catch (e) {
      setError(e?.message || "Could not record that");
    } finally {
      setBusy(null);
    }
  };

  const rows = [
    {
      key: "observation",
      label: "Under observation",
      at: visit.observation_started_at,
      by: visit.observation_started_by_name,
      note: visit.observation_note,
      action: "Place under observation",
    },
  ];

  return (
    <IpdSectionCard
      title="Mid-visit events"
      subtitle="Stages of the visit, not outcomes — the visit stays open through both"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {rows.map((r) => (
          <Box
            key={r.key}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              px: 1.75,
              py: 1.25,
              border: `1px solid ${IPD_COLORS.border}`,
              borderRadius: `${IPD_SHAPE.radiusMd}px`,
              bgcolor: r.at ? IPD_COLORS.pageBg : IPD_COLORS.cardBg,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: r.at ? 700 : 500 }}>
                {r.label}
              </Typography>
              {r.at ? (
                <>
                  <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                    {formatDateTime(r.at)}{r.by ? ` · ${r.by}` : ""}
                  </Typography>
                  {r.note && (
                    <Typography sx={{ mt: 0.3, fontSize: 12.5 }}>{r.note}</Typography>
                  )}
                </>
              ) : (
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted }}>
                  Not recorded
                </Typography>
              )}
            </Box>
            {!r.at && !disabled && (
              <IpdButton
                variant="outlined"
                size="small"
                loading={busy === r.key}
                onClick={() => record(r.key)}
                sx={{ flexShrink: 0 }}
              >
                {r.action}
              </IpdButton>
            )}
          </Box>
        ))}
      </Box>
    </IpdSectionCard>
  );
}

/** Every doctor who has held this visit (ER-PRV-003). */
function ProviderHistory({ history }) {
  if (!history || history.length === 0) {
    return (
      <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary }}>
        No Emergency Provider yet. One is named when the patient is given a bed.
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {history.map((e, i) => (
        <Box key={e.id} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
          <Box
            sx={{
              width: 9, height: 9, mt: 0.6, flexShrink: 0, borderRadius: "50%",
              bgcolor: i === 0 ? IPD_COLORS.primary : IPD_COLORS.borderHover,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: i === 0 ? 700 : 500 }}>
              {e.provider_name || e.provider_id}
              {i === 0 && (
                <Typography component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 600, color: IPD_COLORS.textSecondary }}>
                  current
                </Typography>
              )}
            </Typography>
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
              {formatDateTime(e.assigned_at)}
            </Typography>
            {/* Who handed over: each doctor took over from the one below them in
                the chain; the oldest entry is the first naming, at bed assignment
                (ER-PRV-003). */}
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
              {i < history.length - 1
                ? `Took over from ${history[i + 1].provider_name || history[i + 1].provider_id}`
                : "First named with the bed"}
              {e.assigned_by_name ? ` · by ${e.assigned_by_name}` : ""}
            </Typography>
            {e.reason && (
              <Typography sx={{ mt: 0.3, fontSize: 12.5, fontStyle: "italic", color: IPD_COLORS.textPrimary }}>
                {e.reason}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/** Completing an unnamed patient's identity on the same record (ER-REG-007). */
function IdentityPending({ visit, onSaved, disabled }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [age, setAge] = useState(visit.age ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!name.trim()) {
      setError("Enter the patient's name.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const patch = { patient_name: name.trim() };
      if (age !== "" && age != null) patch.age = Number(age);
      await onSaved(patch);
      setOpen(false);
    } catch (e) {
      setError(e?.message || "Could not save the identity");
    } finally {
      setSaving(false);
    }
  };

  return (
    <IpdSectionCard
      title="Identity pending"
      subtitle={`Working under the temporary label "${visit.temp_label}"`}
      actions={
        !open && !disabled ? (
          <IpdButton startIcon={<BadgeIcon />} onClick={() => setOpen(true)}>
            Complete identity
          </IpdButton>
        ) : null
      }
    >
      <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary }}>
        Fill in the name on this same record. Everything recorded so far — vitals, orders,
        medicines — stays attached to this patient, and the temporary label stops appearing
        everywhere at once.
      </Typography>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" }, gap: 2 }}>
            <Box>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Full name</Typography>
              <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                size="small"
                fullWidth
                autoFocus
                sx={ipdElevatedInputSx}
                inputProps={{ "aria-label": "Full name" }}
              />
            </Box>
            <Box>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Age</Typography>
              <TextField
                value={age}
                onChange={(e) => setAge(e.target.value)}
                size="small"
                fullWidth
                type="number"
                sx={ipdElevatedInputSx}
                inputProps={{ min: 0, max: 130, "aria-label": "Age" }}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 2 }}>
            <IpdButton variant="outlined" onClick={() => setOpen(false)}>Cancel</IpdButton>
            <IpdButton onClick={save} loading={saving}>Save identity</IpdButton>
          </Box>
        </Box>
      </Collapse>
    </IpdSectionCard>
  );
}

function PayerCard({ visit, onSaved, disabled }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() =>
    PAYER_FIELDS.reduce((acc, [k]) => ({ ...acc, [k]: visit.payer?.[k] || "" }), {})
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await onSaved({ payer: form });
      setEditing(false);
    } catch (e) {
      setError(e?.message || "Could not save the payer details");
    } finally {
      setSaving(false);
    }
  };

  const recorded = PAYER_FIELDS.filter(([k]) => visit.payer?.[k]);

  return (
    <IpdSectionCard
      title="Payer details"
      subtitle="Never required to begin treatment"
      actions={
        !editing && !disabled ? (
          <IpdButton variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
            {recorded.length ? "Edit" : "Add"}
          </IpdButton>
        ) : null
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
          {error}
        </Alert>
      )}

      {editing ? (
        <>
          <Box sx={{ display: "grid", gap: 2 }}>
            {PAYER_FIELDS.map(([key, label]) => (
              <Box key={key}>
                <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>{label}</Typography>
                <TextField
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  size="small"
                  fullWidth
                  sx={ipdElevatedInputSx}
                  inputProps={{ "aria-label": label }}
                />
              </Box>
            ))}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 2 }}>
            <IpdButton variant="outlined" onClick={() => setEditing(false)}>Cancel</IpdButton>
            <IpdButton onClick={save} loading={saving}>Save</IpdButton>
          </Box>
        </>
      ) : recorded.length === 0 ? (
        <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary }}>
          No payer recorded. These can be captured at any point in the visit.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {recorded.map(([key, label]) => (
            <IpdDetailRow key={key} label={label} value={visit.payer[key]} />
          ))}
        </Box>
      )}
    </IpdSectionCard>
  );
}

export default function VisitDetail() {
  const { visitId } = useParams();
  const history = useHistory();

  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [triageOpen, setTriageOpen] = useState(false);
  const [bedOpen, setBedOpen] = useState(false);
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [tab, setTab] = useState("overview");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setVisit(await getEmergencyVisit(visitId));
      setError("");
    } catch (e) {
      setVisit(null);
      setError(e?.message || "Could not load this visit");
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  useEffect(() => { load(); }, [load]);

  /** Save, then re-read. Identity completion changes the display name in
   *  several places at once, so the server's copy is the one to trust. */
  const patch = async (body) => {
    const updated = await updateEmergencyVisit(visitId, body);
    // The PATCH response is serialize_visit() only — it omits the collections the
    // GET adds (allergies, risk_assessment, home_medications, the triage/provider
    // histories, orders…). Merge rather than replace, so an identity, payer or
    // duplicate-dismiss edit does not silently wipe them from the page — the
    // allergy banner most of all, which is meant to be impossible to miss.
    setVisit((prev) => ({ ...prev, ...updated }));
    setNotice(
      body.patient_name
        ? "Identity completed — the temporary label has been replaced everywhere."
        : "Visit updated."
    );
    return updated;
  };

  const dismissDuplicate = async () => {
    try {
      await patch({ duplicate_warning_dismissed: true });
    } catch (e) {
      setError(e?.message || "Could not dismiss the warning");
    }
  };

  if (loading) {
    return (
      <Box sx={{ ...ipdPageSx, p: { xs: 2, md: 3 } }}>
        <Skeleton variant="text" width={280} height={44} />
        <Skeleton variant="rounded" height={150} sx={{ my: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }} />
        <Skeleton variant="rounded" height={280} sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }} />
      </Box>
    );
  }

  if (!visit) {
    return (
      <Box sx={{ ...ipdPageSx, p: { xs: 2, md: 3 } }}>
        <EmergencyPlaceholder
          title="This visit could not be opened"
          description={
            error ||
            "It may belong to another facility — staff only ever see their own hospital's emergency visits."
          }
          action={
            <IpdButton startIcon={<ArrowBackIcon />} onClick={() => history.push("/emergency_tracking_board")}>
              Back to the board
            </IpdButton>
          }
        />
      </Box>
    );
  }

  const closed = visit.status === "closed";
  const stage = visitStage(visit);
  const ArrivalIcon = visit.arrival_mode === "ambulance" ? LocalTaxiIcon : DirectionsWalkIcon;
  const unnamed = !visit.patient_name;

  return (
    <Box sx={{ ...ipdPageSx, p: { xs: 2, md: 3 } }}>
      <IpdPageHeader
        eyebrow="Emergency Visit"
        title={visit.display_name}
        description={`${visit.visit_number} · ${visit.patient_id || "no hospital id"}`}
        actions={
          <>
            <IpdButton
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push("/emergency_tracking_board")}
            >
              Board
            </IpdButton>
            {!closed && (
              <IpdButton variant="outlined" onClick={() => setTriageOpen(true)}>
                {visit.triage_category ? "Re-triage" : "Triage"}
              </IpdButton>
            )}
            {!closed && (
              <IpdButton onClick={() => setBedOpen(true)}>
                {visit.bed_id ? "Move bed" : "Assign bed"}
              </IpdButton>
            )}
          </>
        }
      />

      {/* Allergies first and loudest — the one thing on this page most dangerous
          to miss (ticket 15). Shown on every tab, not tucked inside one. */}
      {visit.allergies && visit.allergies.length > 0 && (
        <Alert
          severity="error"
          icon={<WarningAmberIcon />}
          sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 800 }}>
            Allergies
          </Typography>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textPrimary }}>
            {visit.allergies
              .map((a) => a.allergen + (a.severity ? ` (${a.severity})` : ""))
              .join("  ·  ")}
          </Typography>
        </Alert>
      )}

      {notice && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      {error && visit && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {closed && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
          This visit is closed. A closed visit is never reopened — its record is read-only here.
        </Alert>
      )}
      {visit.duplicate_warning && !visit.duplicate_warning_dismissed && (
        <Alert
          severity="warning"
          sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
          action={
            <IpdButton variant="outlined" size="small" onClick={dismissDuplicate}>
              Dismiss
            </IpdButton>
          }
        >
          {visit.duplicate_warning}
        </Alert>
      )}
      {/* Dismissing quiets the banner but must not hide the possible match: it
          stays here, quieter and without a dismiss button, so staff can still
          reconcile it afterwards (ER-REG-010). */}
      {visit.duplicate_warning && visit.duplicate_warning_dismissed && (
        <Alert
          severity="info"
          sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
            Possible duplicate — dismissed, kept so it can still be reconciled
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
            {visit.duplicate_warning}
          </Typography>
        </Alert>
      )}

      {/* Patient banner */}
      <IpdSectionCard sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mb: 1.5 }}>
          <Typography
            sx={{
              fontSize: 19,
              fontWeight: 700,
              color: IPD_COLORS.textPrimary,
              fontStyle: unnamed ? "italic" : "normal",
            }}
          >
            {visit.display_name}
          </Typography>
          <TriageBadge category={visit.triage_category} />
          <StageBadge stage={stage} />
          {visit.is_mlc && <MlcBadge caseNumber={visit.mlc_case_number} />}
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, mb: 1.5 }}>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
            {visit.age ? `${visit.age} yrs` : "Age unknown"} · {GENDER_LABEL[visit.gender] || visit.gender}
          </Typography>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, display: "flex", alignItems: "center", gap: 0.5 }}>
            <ArrivalIcon sx={{ fontSize: 15 }} />
            {ARRIVAL_MODE_LABEL[visit.arrival_mode] || "Arrival not recorded"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
            Arrived {formatDateTime(visit.arrived_at)} ({waitingSince(visit.arrived_at)} ago)
          </Typography>
          <Typography sx={{ fontSize: 13, color: visit.bed_label ? IPD_COLORS.textPrimary : IPD_COLORS.textMuted, fontWeight: visit.bed_label ? 700 : 400 }}>
            {visit.bed_label || "No bed assigned"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: visit.provider_name ? IPD_COLORS.textPrimary : IPD_COLORS.textMuted }}>
            {visit.provider_name || "No provider"}
          </Typography>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Typography sx={{ fontSize: 13.5 }}>
          <strong>Chief complaint: </strong>
          {visit.chief_complaint || "—"}
        </Typography>
        {unnamed && visit.identification_mark && (
          <Typography sx={{ mt: 1, fontSize: 13.5, color: IPD_COLORS.textSecondary }}>
            <strong style={{ color: IPD_COLORS.textPrimary }}>Identification mark: </strong>
            {visit.identification_mark}
          </Typography>
        )}
      </IpdSectionCard>

      {/* The visit's record split into tabs — each area its own focused view
          rather than one long scroll. The header, alerts and patient banner
          above stay put; only the section below the tab bar changes. */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <IpdTabBar tabs={VISIT_TABS} activeTab={tab} onChange={setTab} ariaLabel="Visit sections" />
      </Box>

      {tab === "overview" && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" }, gap: 2, alignItems: "start" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {unnamed && !closed && (
              <IdentityPending visit={visit} onSaved={patch} disabled={closed} />
            )}
            <AllergiesCard
              visit={visit}
              disabled={closed}
              onChanged={async () => {
                await load();
                setNotice("Allergies updated.");
              }}
            />
            <AssessmentCard
              visit={visit}
              disabled={closed}
              onSaved={async () => {
                await load();
                setNotice("Assessment saved.");
              }}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <IpdSectionCard
              title="Emergency Provider"
              subtitle="Responsibility for this visit only"
              actions={
                // A handover changes an existing provider; the first one is named
                // with the bed, so this appears only once there is someone to hand
                // over from and the visit is still open.
                !closed && visit.provider_id ? (
                  <IpdButton
                    variant="outlined"
                    size="small"
                    startIcon={<SwapHorizIcon />}
                    onClick={() => setHandoverOpen(true)}
                  >
                    Hand over
                  </IpdButton>
                ) : null
              }
            >
              <ProviderHistory history={visit.provider_history} />
            </IpdSectionCard>
            <IpdSectionCard title="Key timestamps">
              <Timeline visit={visit} />
            </IpdSectionCard>
            <IpdSectionCard title="Registration">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <IpdDetailRow label="Visit number" value={visit.visit_number} />
                <IpdDetailRow label="Hospital identifier" value={visit.patient_id} />
                <IpdDetailRow label="Health-record link" value={visit.health_record_id} />
                <IpdDetailRow label="Registered by" value={visit.registered_by_name || "—"} />
              </Box>
            </IpdSectionCard>
            <PayerCard visit={visit} onSaved={patch} disabled={closed} />
            <HomeMedicationsCard
              visit={visit}
              disabled={closed}
              onChanged={async () => {
                await load();
                setNotice("Home medications updated.");
              }}
            />
          </Box>
        </Box>
      )}

      {tab === "vitals" && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" }, gap: 2, alignItems: "start" }}>
          <VitalsCard
            visit={visit}
            disabled={closed}
            onRecorded={(message) => setNotice(message || "Vitals recorded.")}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <IpdSectionCard title="Triage history" subtitle="Earlier categories are kept, never overwritten">
              <TriageHistory history={visit.triage_history} />
            </IpdSectionCard>
            <RiskAssessmentCard
              visit={visit}
              disabled={closed}
              onChanged={async () => {
                await load();
                setNotice("Risk assessment updated.");
              }}
            />
          </Box>
        </Box>
      )}

      {tab === "orders" && (
        <OrdersCard
          visit={visit}
          disabled={closed}
          onPlaced={async (message) => {
            await load();
            setNotice(message || "Orders placed.");
          }}
        />
      )}

      {tab === "meds" && (
        <MedicationsCard
          visit={visit}
          disabled={closed}
          onRecorded={(message) => setNotice(message || "Dose recorded.")}
        />
      )}

      {tab === "charges" && (
        <ChargesCard
          visit={visit}
          disabled={closed}
          // Re-read the visit: the first charge opens its billing account, and
          // the account id is stamped on the visit rather than returned here.
          onChanged={load}
        />
      )}

      {tab === "mlc" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <MedicoLegalCard
            visit={visit}
            disabled={closed}
            onChanged={async () => {
              await load();
              setNotice("Medico-legal record updated.");
            }}
          />
          {/* Only for a live medico-legal case — the report belongs to one. */}
          {visit.is_mlc && (
            <InjuryReportCard
              visit={visit}
              disabled={closed}
              onChanged={async () => {
                await load();
                setNotice("Injury report updated.");
              }}
            />
          )}
        </Box>
      )}

      {tab === "disposition" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <MidVisitEvents
            visit={visit}
            disabled={closed}
            onRecord={async (event) => {
              await recordStageEvent(visit.id, { event });
              await load();
              setNotice("Patient placed under observation — still open on the board.");
            }}
          />
          {/* Directly above the disposition, because closing as Discharged is
              refused without it. Still shown on a closed visit: a patient who
              lost their copy comes back for another. */}
          {(!closed || visit.discharge_summary) && (
            <DischargeSummaryCard visit={visit} disabled={closed} onChanged={load} />
          )}
          <DispositionCard
            visit={visit}
            onClosed={async (message) => {
              await load();
              setNotice(message || "Visit closed.");
            }}
          />
          {/* Death registration — present once the visit is closed as expired.
              Certifying is a deliberate action on the closed visit. */}
          {visit.death_record && (
            <DeathRegistrationCard
              visit={visit}
              onCertified={async () => {
                await load();
                setNotice("Death certified.");
              }}
            />
          )}
          {visit.death_record && (
            <DeathCauseCard
              visit={visit}
              onChanged={async () => {
                await load();
                setNotice("Cause of death recorded.");
              }}
            />
          )}
          {visit.death_record && (
            <DeathCertificateCard
              visit={visit}
              onChanged={async () => {
                await load();
                setNotice("Death record updated.");
              }}
            />
          )}
        </Box>
      )}

      <TriageDialog
        open={triageOpen}
        visit={visit}
        onClose={() => setTriageOpen(false)}
        onTriaged={() => {
          // Re-read rather than merging: the response carries the visit but not
          // the rebuilt history, and the history panel is the point here.
          load();
          setNotice("Triage recorded.");
        }}
      />

      <AssignBedDialog
        open={bedOpen}
        visit={visit}
        onClose={() => setBedOpen(false)}
        onAssigned={() => {
          load();
          setNotice("Bed assigned and Emergency Provider named.");
        }}
      />

      <ProviderHandoverDialog
        open={handoverOpen}
        visit={visit}
        onClose={() => setHandoverOpen(false)}
        onHandedOver={(message) => {
          // Re-read rather than merging: the response carries the visit but not
          // the rebuilt provider history, and the chain is the point of the card.
          load();
          setNotice(message || "Emergency Provider handed over.");
        }}
      />
    </Box>
  );
}
